import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import TaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { FILE_TYPE_VALUE } from '@nxsys/utils';
import { getRangeYear } from '@nxsys/utils/taxYear';
import { Alert, Form, FormProps, Radio } from 'antd';
import { orderBy } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';

type ExportFilterModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onExport: (values: any) => void;
};

const ExportFilterModal: React.FC<ExportFilterModalProps> = ({
  isOpen,
  toggle,
  onExport,
}: ExportFilterModalProps) => {
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const taxYearRange = orderBy(getRangeYear(isIreland), 'value', 'desc');

  const [form] = Form.useForm();
  const watchFileType = Form.useWatch('fileType', form);
  const watchAllTaxPeriod = Form.useWatch('allTaxPeriod', form);

  const fileTypeOptions = [
    ...(isBureau
      ? [
          {
            label: messages['export.company.all'],
            value: FILE_TYPE_VALUE.ALL,
          },
        ]
      : [
          {
            label: messages['common.agency'],
            value: FILE_TYPE_VALUE.AGENCY,
          },
        ]),
    {
      label: messages['export.company.employee'],
      value: FILE_TYPE_VALUE.SUBCONTRACTOR,
    },
    {
      label: messages['export.company.payroll'],
      value: FILE_TYPE_VALUE.PAYROLL,
    },
    {
      label: messages['export.company.payslip'],
      value: FILE_TYPE_VALUE.SELF_BILL,
    },
  ];

  const FORM_ITEMS = [
    {
      label: messages['export.company.fileType'],
      name: 'fileType',
      type: 'select',
      options: fileTypeOptions,
      placeholder: messages['export.company.selectFileType'],
      required: true,
    },
    ...(watchFileType &&
    watchFileType !== FILE_TYPE_VALUE.SUBCONTRACTOR &&
    watchFileType !== FILE_TYPE_VALUE.AGENCY &&
    watchFileType !== FILE_TYPE_VALUE.ALL
      ? [
          {
            label: 'Export Period',
            name: 'allTaxPeriod',
            type: 'radio',
            options: [
              { label: 'Specific Period', value: false },
              { label: 'All Tax Periods', value: true },
            ],
            required: false,
          },
          ...(!watchAllTaxPeriod
            ? [
                {
                  label: messages['table.taxPeriod'],
                  name: 'taxPeriod',
                  type: 'selectTaxWeekMonth',
                  required: true,
                },
              ]
            : []),
          {
            label: messages['common.taxYear'],
            name: 'taxYear',
            type: 'select',
            options: taxYearRange.map((option) => ({
              label: option.label,
              value: option.value.toString(),
            })),
            required: true,
          },
        ]
      : []),
  ];

  const onFinish: FormProps['onFinish'] = (values) => {
    onExport(values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const handleTaxWeekChange = (value: number | undefined) => {
    form.setFieldsValue({
      taxPeriod: value,
    });
  };

  return (
    <AppModalContainer
      title={messages['export.company.selectOptions']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.export'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      minHeight="0px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{
          allTaxPeriod: false,
          taxYear: taxYearRange[0]?.value?.toString(),
        }}
      >
        {FORM_ITEMS.map((formItem) => (
          <Form.Item
            key={formItem.name}
            label={formItem.label}
            name={formItem.name}
            rules={[
              {
                required: formItem.required,
              },
            ]}
          >
            {formItem.type === 'select' && (
              <AppSelect
                placeholder={formItem.placeholder}
                options={formItem.options ?? []}
                onChange={(value) => {
                  form.setFieldsValue({
                    [formItem.name]: value.value,
                  });
                }}
              />
            )}
            {formItem.type === 'selectTaxWeekMonth' && (
              <TaxWeekMonthSelect
                placeholder={formItem.placeholder}
                onFilter={(e) => handleTaxWeekChange(e.taxPeriod)}
                paymentFrequency="WEEKLY"
              />
            )}
            {formItem.type === 'radio' && (
              <Radio.Group options={formItem.options ?? []} />
            )}
          </Form.Item>
        ))}
        {(watchFileType === FILE_TYPE_VALUE.ALL || watchAllTaxPeriod) && (
          <Alert
            type="warning"
            showIcon
            className="mt-2"
            message="Exporting all data can take a long time to complete."
          />
        )}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default ExportFilterModal;
